/**
 * Coach Engine — Account Scope
 *
 * Per-account observation orchestrator. Filters trades to the named
 * account, runs registered account-scope analyzers, returns ranked
 * observations.
 *
 * Different from setup/atm/day/progress scopes:
 *   - day:      one date, all setups/ATMs/accounts
 *   - progress: cross-session 30D trends
 *   - setup:    one setup, all of its history
 *   - atm:      one ATM strategy, all of its history
 *   - account:  one account, comparing to baseline + cross-account
 *
 * Where setup/ATM scopes ask "what about this signal works?", account scope
 * asks "what about this ACCOUNT specifically explains the variance?". Useful
 * because the trader runs 7+ accounts simultaneously; per-account behavior
 * diverges in ways that get averaged out at other scopes.
 *
 * Adding a new account-scope analyzer:
 *   1. Create the module under ../analyzers/account/
 *   2. Push into ANALYZERS below
 *   3. Done — ranking + dispatch wire up in index.js
 */

import { topObservations } from '../priorities';
import { synthesizeAndPrepend } from '../synthesis/synthesize';
import { enrichWithTemporalContext } from '../synthesis/temporalContext';
import { getRuntimeContext } from '../profile/runtimeContext';
import { buildEmptyScopeHint } from '../emptyScopeHint';
import * as accountPerformance from '../analyzers/account/accountPerformance';
import * as accountAsymmetry   from '../analyzers/account/accountAsymmetry';

const ANALYZERS = [
  accountPerformance,
  accountAsymmetry,
];

const TIER_LEVEL = { free: 0, pro: 1 };

const analyzersForTier = (userTier = 'free') => {
  const level = TIER_LEVEL[userTier] ?? 0;
  return ANALYZERS.filter(a => (TIER_LEVEL[a.tier || 'free'] ?? 0) <= level);
};

/**
 * Run the registered account-scope analyzers.
 *
 * @param {Object} ctx
 * @param {string} ctx.accountId           The account being analyzed (matched against trade.accountId)
 * @param {string} [ctx.accountLabel]      Display label (account name); defaults to accountId
 * @param {Array}  ctx.allTrades           Full trades array — analyzer filters to this account
 *                                          AND uses the rest as cross-account baseline
 * @param {string} [ctx.anchorDate]
 * @param {string} [ctx.tier]
 * @param {number} [ctx.top]
 * @param {Function} [ctx.getTradeNetPnL]
 * @param {Function} [ctx.getAccountById]
 * @returns {Array<Observation>}
 */
export const analyzeAccount = (ctx = {}) => {
  const {
    accountId, accountLabel, allTrades = [], anchorDate, tier = 'free', top,
    getTradeNetPnL, getAccountById,
  } = ctx;
  if (!accountId) return [];
  if (!Array.isArray(allTrades) || allTrades.length === 0) return [];

  // Filter to this account first.
  const accountTrades = allTrades.filter(t => t && t.accountId === accountId);
  if (accountTrades.length === 0) return [];

  // Enrich with commission-aware netPnl. Same reasoning as setup + atm
  // scopes: analyzers read t.netPnl > 0 to count winners; raw trade
  // objects don't carry netPnl.
  const netPnlFor = (t) => {
    const acc = getAccountById ? getAccountById(t.accountId) : null;
    const r = getTradeNetPnL ? getTradeNetPnL(t, acc) : { netPnl: parseFloat(t.pnl) || 0 };
    return Number.isFinite(r?.netPnl) ? r.netPnl : 0;
  };
  // Enrich BOTH the focal account's trades AND the full corpus — analyzers
  // that compare focal to baseline need consistent netPnl on both sides.
  const enrichedAccountTrades = accountTrades.map(t => ({ ...t, netPnl: netPnlFor(t) }));
  const enrichedAllTrades = allTrades.map(t => ({ ...t, netPnl: netPnlFor(t) }));

  const label = accountLabel || (getAccountById && getAccountById(accountId)?.name) || accountId;

  const analyzers = analyzersForTier(tier);
  const all = [];
  for (const analyzer of analyzers) {
    try {
      const result = analyzer.analyze({
        accountId,
        accountLabel: label,
        accountTrades: enrichedAccountTrades,
        allTrades: enrichedAllTrades,
        anchorDate,
        tier,
        getTradeNetPnL,
        getAccountById,
      });
      if (Array.isArray(result)) all.push(...result);
      else if (result) all.push(result);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`[Coach account] ${analyzer.key || 'unknown'} failed:`, e?.message || e);
    }
  }

  if (all.length === 0) {
    const hint = buildEmptyScopeHint({ scope: 'account', scopeId: label, tradeCount: enrichedAccountTrades.length });
    return hint ? [hint] : [];
  }
  // Profile-aware enrichment + synthesis (same shape as other scopes).
  const enriched = enrichWithTemporalContext(all, getRuntimeContext());
  const withSynthesis = synthesizeAndPrepend(enriched, { scope: 'account', scopeId: label });
  return topObservations(withSynthesis, top);
};
